import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';
import { first, map, Observable, of, tap } from 'rxjs';
import { User } from '../../state/auth/types';
import { Customer } from '../../models';
import { BackendTemplate } from '../../components/custom-page/custom-page-interface/template-component';
import { ConfigurationsService } from '../configurations.service';
import { HomePageMatchGroups } from '../../components/home-navigation-config/home-navigation-config.component';
import { GungFlow } from '../../state/flow/types';
@Injectable({
  providedIn: 'root'
})
export class HomeNavigationConfigService {
  adminLink = ['/sales-dashboard'];
  salesLink = ['/sales-dashboard'];
  multiCustomersUserLink = ['/delivery-location'];
  singleCustomerUserLink = ['/categories'];
  anonymousLink = ['/products'];
  supplierBuyerLink = ['/supplier-portal'];
  public dynamicCustomPageByGroups = false;
  homePageName = 'home';
  homeGroupPageName = 'home-group';
  homeGroupPageNameList: string[]; // insted of homeGroupPageName
  ignoreHomeGroupPageName: boolean = false;
  public homepageDateRangeEnabled: boolean = false;
  public homepageCountryEnabled: boolean = false;

  /*
   * Key is the role, value is the link to navigate to
   * Configured from the backend. If we have values in here, they should trump the default values.
   */
  roleToLinkConfig: { [role: string]: string } = {};

  constructor(
    protected authService: AuthService,
    protected router: Router,
    protected configurationsService: ConfigurationsService
  ) { }

  /**
   * Checks if the configurations are loaded and loads them otherwise.
   * Required to be called before getHomeLink is called the first time in order for the backend configuration to be
   * loaded correctly.
   */
  isLoaded(): Observable<boolean> {
    return this.configurationsService.getConfigurationsByIds(['HomePageConfiguration']).pipe(
      first(),
      tap(config => {
        if (!!config && config.length > 0) {
          this.roleToLinkConfig = config[0]['roleHomePages'];
        }
      }),
      map(() => true)
    );
  }

  setAdminHomeLink(link: string[]) {
    this.adminLink = link;
  }

  setSalesHomeLink(link: string[]) {
    this.salesLink = link;
  }

  setMultiCustomersUserLink(link: string[]) {
    this.multiCustomersUserLink = link;
  }

  setSingleCustomerUserLink(link: string[]) {
    this.singleCustomerUserLink = link;
  }

  setAnonymousHomeLink(link: string[]) {
    this.anonymousLink = link;
  }

  getHomeLink(user: User, customers?: Customer[], customPages?: BackendTemplate[]): string[] {
    if (this.getHomeLinkByBackendConfiguration(user)) {
      return [this.getHomeLinkByBackendConfiguration(user)];
    }

    if (this.dynamicCustomPageByGroups && customPages?.length > 0) {
      const activeGroups = Object.keys(user.activeGroups).filter(ag => !!user.activeGroups[ag]);
      const pagesByGroup = customPages.filter(
        p => activeGroups.filter(ag => p.activeForUserGroups?.includes(ag)).length > 0
      );
      const homePage = pagesByGroup.find(p => p.pageName === this.homePageName);
      if (homePage) {
        return ['page', homePage.pageName];
      }
      const homeGroupPage =
        this.homeGroupPageNameList?.length > 0
          ? pagesByGroup.find(p => this.homeGroupPageNameList.includes(p.pageName))
          : pagesByGroup.find(p => p.pageName.startsWith(this.homeGroupPageName));
      if (homeGroupPage) {
        return ['page', homeGroupPage.pageName];
      }

      if (this.ignoreHomeGroupPageName) {
        return ['page', pagesByGroup[0].pageName];
      }
    }
    if (user.roles.includes('SUPPLIER') || user.roles.includes('BUYER')) {
      return this.supplierBuyerLink;
    } else if (user.roles.includes('ADMIN')) {
      return this.adminLink;
    } else if (user.roles.includes('SALES')) {
      return this.salesLink;
    } else if (user.roles.includes('USER')) {
      if (customers && customers.length > 1) {
        return this.multiCustomersUserLink;
      } else {
        return this.singleCustomerUserLink;
      }
    } else if (user.roles.includes('ANONYMOUS')) {
      return this.anonymousLink;
    }
  }

  getHomeLinkByBackendConfiguration(user: User): string | undefined {
    const defaultRoles: Set<string> = new Set(['SUPPLIER', 'BUYER', 'ADMIN', 'SALES', 'USER', 'ANONYMOUS']);
    const nonDefaultRole = user.roles.find(role => !defaultRoles.has(role));
    if (!!nonDefaultRole && !!this.roleToLinkConfig[nonDefaultRole]) {
      // If the user has a role that is not in the default roles, prioritize that role's link
      return this.roleToLinkConfig[nonDefaultRole];
    }

    if (user.roles.includes('SUPPLIER')) {
      return this.roleToLinkConfig['SUPPLIER'];
    } else if (user.roles.includes('BUYER')) {
      return this.roleToLinkConfig['BUYER'];
    } else if (user.roles.includes('ADMIN')) {
      return this.roleToLinkConfig['ADMIN'];
    } else if (user.roles.includes('SALES')) {
      return this.roleToLinkConfig['SALES'];
    } else if (user.roles.includes('USER')) {
      return this.roleToLinkConfig['USER'];
    } else if (user.roles.includes('ANONYMOUS')) {
      return this.roleToLinkConfig['ANONYMOUS'];
    }
    return undefined;
  }

  public getDynamicHomeLink(
    homePageMatchGroups: HomePageMatchGroups[],
    user: User,
    userRoles: string[],
    selectedFlow: GungFlow,
    customer?: Customer
  ): string[] {
    const userGroups: string[] = Object.entries(user.activeGroups).filter(([role, isActive]) => !!isActive).map(([role, isActive]) => role);
    const now = Date.now();

    const matchedGroups = homePageMatchGroups.filter(pageMatch => {
      return (
        (pageMatch.roles.length === 0 ||
          this.checkIfArraysIntersect(pageMatch.roles, userRoles)) &&

        (pageMatch.groups.length === 0 ||
          this.checkIfArraysIntersect(pageMatch.groups, userGroups)) &&

        (pageMatch.flowIds.length === 0 ||
          pageMatch.flowIds.includes(selectedFlow.id)) &&

        this.isWithinDateRange(pageMatch, now) &&

        this.isMatchingCountry(pageMatch, customer)
      );
    });

    if (matchedGroups.length > 0) {
      return [matchedGroups[0].pagePath];
    }

    return [];
  }

  protected isWithinDateRange(pageMatch: HomePageMatchGroups, now: number): boolean {
    if (!this.homepageDateRangeEnabled) {
      return true;
    }
    const startOk = pageMatch.startDate == null || now >= pageMatch.startDate;
    const endOk = pageMatch.endDate == null || now <= pageMatch.endDate;
    return startOk && endOk;
  }

  protected isMatchingCountry(pageMatch: HomePageMatchGroups, customer?: Customer): boolean {
    if (!this.homepageCountryEnabled || !pageMatch.countryCodes?.length) {
      return true;
    }
    const countryCode = this.getCustomerCountryCode(customer);
    return !!countryCode && pageMatch.countryCodes.includes(countryCode);
  }

  public getCustomerCountryCode(customer?: Customer): string | undefined {
    return customer?.extra?.gme?.address?.countryCode;
  }

  public getAvailableCountryCodes(): Observable<string[]> {
    return of([]);
  }

  protected checkIfArraysIntersect(a: string[], b: string[]): boolean {
    const setA = new Set(a);
    return b.some(item => setA.has(item));
  }
}
